import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CForm,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
  CFormInput,
  CInputGroup,
} from "@coreui/react-pro";
import { getMotorPredefined, updateMotorPredefined } from "../../utils/backend";

import { CPagination, CPaginationItem } from "@coreui/react-pro";

import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const hasPermission = (permissionGroup, permissionName) => {
  let permissions;
  try {
    permissions = JSON.parse(localStorage.getItem("permissions")) || [];
  } catch (error) {
    console.error("Error parsing permissions from localStorage:", error);
    permissions = [];
  }

  return permissions.some(
    (perm) =>
      perm.permission_group === permissionGroup &&
      perm.permission_name === permissionName
  );
};

const MotorProduct = () => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });
  const [filters, setFilters] = useState({
    plan_name: "",
    veh_type: "",
    insurance_name: "",
    plan_status: "",
    start_date: "",
    end_date: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    const savedState = JSON.parse(localStorage.getItem("predefinedState"));
    if (savedState) {
      setFilters(savedState.filters || {});
      setPerPage(savedState.perPage || 10);
      setTotalItems(savedState.totalItems || 0);
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      fetchData(
        savedState.filters || {},
        1,
        savedState.perPage || 10
      );
    } else {
      // Use default values for the first-time load
      const defaultFilters = {
        plan_name: "",
        veh_type: "",
        insurance_name: "",
        plan_status: true,
        start_date: "",
        end_date: "",
      };

      fetchData(defaultFilters, 1, 10);
    }
  }, []);

  const fetchData = async (filters, page, perPage) => {
    // setIsLoading(true);
    try {
      const response = await getMotorPredefined({
        ...filters,
        page,
        perPage,
      });

      if (response.success) {
        setServices(setUppercase(response.data.data));
        setCurrentPage(response.data.currentPage);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
      } else {
        toast.error("Failed to fetch data");
        setServices([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("An error occurred while fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (serviceId, newStatus) => {
    if (!hasPermission("Product Service Management", "Edit")) {
      toast.error("You do not have permission to change status");
      return;
    }
    const updatedStatus = newStatus;

    // Find the original service and store its status
    const originalService = services.find(
      (service) => service.id === serviceId
    );
    const originalStatus = originalService ? originalService.status : null;
    // If the new status is the same as the current status, do nothing
    if (originalStatus === updatedStatus) {
      return;
    }

    // Optimistically update the UI
    setServices((prevLogs) =>
      prevLogs.map((service) =>
        service.id === serviceId
          ? { ...service, plan_status: updatedStatus }
          : service
      )
    );

    try {
      const response = await updateMotorPredefined(serviceId, {
        plan_status: newStatus,
      });
      if (response.success) {
        toast.success("Status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");

      // Revert the status if the update fails
      setServices((prevLogs) =>
        prevLogs.map((service) =>
          service.id === serviceId
            ? { ...service, plan_status: originalStatus }
            : service
        )
      );
    }
  };

  const saveStateToLocalStorage = (filters, perPage) => {
    localStorage.setItem(
      "predefinedState",
      JSON.stringify({ filters, perPage })
    );
  };

  const handleSearch = (e) => {
    saveStateToLocalStorage(filters, perPage);
    fetchData(filters, 1, perPage);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    filters.plan_name,
    filters.veh_type,
    filters.plan_status,
    filters.insurance_name,
    filters.start_date,
    filters.end_date,
    perPage,
  ]);

  const handleReset = () => {
    const resetFilters = {
      plan_name: "",
      veh_type: "",
      insurance_name: "",
      plan_status: "",
      start_date: "",
      end_date: "",
    };
    setFilters(resetFilters);
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState =
      JSON.parse(localStorage.getItem("predefinedState")) || {};
    localStorage.setItem(
      "predefinedState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    // Pass reset filters, page, and perPage explicitly
    fetchData(resetFilters, 1, existingState.perPage || 10);
  };

  const handlePageChange = (page) => {
    saveStateToLocalStorage(filters, perPage);
    fetchData(filters, page, perPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    saveStateToLocalStorage(filters, newPerPage);
    fetchData(filters, 1, newPerPage);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setServices((prevServices) => [...prevServices]); // Trigger re-render to apply styles
  };

  const sortedServices = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return services;
    return [...services].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [services, sortConfig]);

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    filters.plan_name,
    filters.veh_type,
    filters.insurance_name,
    filters.plan_status,
    filters.start_date,
    filters.end_date,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 gap-3 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  <CButton
                    className="ansa-button-primary"
                    onClick={() => navigate("/motor-insurance/create")}
                  >
                    Create Plan
                  </CButton>
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <CTable borderless striped style={{ minHeight: "200px" }}>
                <CTableHead>
                  <CTableRow>
                    <CTableDataCell></CTableDataCell>
                    <CTableDataCell></CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={filters.plan_name}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            plan_name: e.target.value,
                          })
                        }
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={filters.veh_type}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            veh_type: e.target.value,
                          })
                        }
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormSelect
                        id="plan_status"
                        value={String(filters.plan_status ?? "")}
                        onChange={(e) => {
                          const val = e.target.value;

                          // 2. Convert String input to Boolean
                          let boolValue = "";
                          if (val === "true") boolValue = true;
                          if (val === "false") boolValue = "false";

                          setFilters({
                            ...filters,
                            plan_status: boolValue,
                          });
                        }}
                      >
                        <option value="">Select Status</option>
                        <option value="true">Active</option>
                        <option value="false">InActive</option>
                      </CFormSelect>
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={filters.insurance_name}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            insurance_name: e.target.value,
                          })
                        }
                      />
                    </CTableDataCell>

                    <CInputGroup style={{ maxWidth: "250px" }}>
                      <CFormInput
                        type="date"
                        value={filters.start_date}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            start_date: e.target.value,
                          })
                        }
                      />
                      <span className="input-group-text bg-light text-muted small">
                        to
                      </span>
                      <CFormInput
                        type="date"
                        value={filters.end_date}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            end_date: e.target.value,
                          })
                        }
                      />
                    </CInputGroup>
                  </CTableRow>
                  <CTableRow>
                    {[
                      { key: "id", label: "ID" },
                      { key: "action", label: "Actions" },
                      { key: "plan_name", label: "Plan Name" },
                      { key: "veh_type", label: "Vehicle Type" },
                      { key: "plan_status", label: "Plan Status" },
                      { key: "insurance_name", label: "Insurance Name" },
                      { key: "updatedAt", label: "Last Updated" },
                    ].map((column) => (
                      <CTableHeaderCell
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                        className={`hover-header ${
                          sortConfig.key === column.key
                            ? "ctable-active-header"
                            : ""
                        }`}
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}{" "}
                      </CTableHeaderCell>
                    ))}
                    {/* <CTableHeaderCell>Actions</CTableHeaderCell> */}
                  </CTableRow>
                </CTableHead>

                <CTableBody>
                  {sortedServices.map((service) => (
                    <CTableRow key={service.id}>
                      <CTableDataCell>{service.id}</CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex align-items-center gap-2">
                          {hasPermission(
                            "Product Service Management",
                            "View"
                          ) && (
                            <CButton
                              className="btn btn-sm btn-info text-white"
                              title="Details"
                              onClick={() =>
                                navigate(
                                  `/motor-insurance/details/${service.id}`
                                )
                              }
                            >
                              <i className="bi bi-eye-fill"></i>
                            </CButton>
                          )}
                          {hasPermission(
                            "Product Service Management",
                            "Edit"
                          ) && ( // Check for permission
                            <CButton
                              className="btn btn-sm btn-warning text-white"
                              title="Edit"
                              onClick={() =>
                                navigate(`/motor-insurance/edit/${service.id}`)
                              }
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </CButton>
                          )}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        {service.plan_name || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {service.veh_type || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CDropdown>
                          <CDropdownToggle
                            color="secondary"
                            variant="outline"
                            disabled={
                              !hasPermission(
                                "Product Service Management",
                                "Edit"
                              )
                            }
                            style={{
                              backgroundColor: service.plan_status
                                ? "#66BB6A"
                                : "#8B0000",
                              color: "#fff",
                              border: "none",
                              width: "150px",
                              borderRadius: "20px",
                              fontWeight: "500",
                              fontSize: "14px",
                            }}
                          >
                            {service.plan_status ? "ACTIVE" : "INACTIVE"}
                          </CDropdownToggle>
                          <CDropdownMenu>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(service.id, true)
                              }
                            >
                              ACTIVE
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(service.id, false)
                              }
                            >
                              INACTIVE
                            </CDropdownItem>
                          </CDropdownMenu>
                        </CDropdown>
                      </CTableDataCell>
                      <CTableDataCell>
                        {service.insurance_name || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {service.updated_at
                          ? formatDateTime(service.updated_at)
                          : "N/A"}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(filters, page, perPage);
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {services.length} of {totalItems} services
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default MotorProduct;
